import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import type { EmailOtpType } from '@supabase/supabase-js'
import { Loader2 } from 'lucide-react'
import { useTranslations } from 'use-intl'
import { Alert, AlertDescription } from '@/components/ui/alert'

// Handles email invite/magic-link tokens of the form:
//   /auth/confirm?token_hash=<hash>&type=invite&next=/accept-invite?token=xxx
//
// The client-side supabase.auth.verifyOtp() call verifies the token and
// establishes a session in localStorage, then we navigate to `next`.
export default function AuthConfirm() {
  const t = useTranslations('consultantOnboarding.app.authConfirm')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const token_hash = searchParams.get('token_hash')
    const type = searchParams.get('type') as EmailOtpType | null
    const next = searchParams.get('next') ?? '/overview'

    if (!token_hash || !type) {
      navigate('/auth')
      return
    }

    supabase.auth
      .verifyOtp({ token_hash, type })
      .then(({ error: verifyError }) => {
        if (verifyError) {
          setError(verifyError.message)
        } else {
          navigate(next, { replace: true })
        }
      })
  }, [])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md space-y-4">
          <Alert variant="destructive">
            <AlertDescription>{t('invalid')}</AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="text-sm">{t('verifying')}</p>
      </div>
    </div>
  )
}
